import React, { useEffect, useCallback } from 'react';
import { Segmented, Tooltip } from 'antd';
import { Sparkles, TextCursorInput } from 'lucide-react';
import { RightPanelProvider, useRightPanelContext } from './RightPanelContext';
import { PANEL_MODE, ACTION_TYPE, PANEL_TAB } from '../constants';
import { REWRITE_PHASE } from '../../hooks/useRewriteTask';
import { extractBlockIdsFromSelection } from '../../utils/blockConverter';

import EmptyPanel from './panels/EmptyPanel';
import SelectionPanel from './panels/SelectionPanel';
import ImagePanel from './panels/ImagePanel';
import TablePanel from './panels/TablePanel';
import FormulaPanel from './panels/FormulaPanel';
import ChapterRewritePanel from './panels/ChapterRewritePanel';

import styles from '../../styles/index.module.scss';

/**
 * 右侧工具面板：章节改稿 / 局部修改
 */
export const EditorRightPanel = ({ onApplyTextResult }) => {
    return (
        <RightPanelProvider onApplyTextResult={onApplyTextResult}>
            <RightPanelContent />
        </RightPanelProvider>
    );
};

// ========== 选中检测（纯函数） ==========

const isInProtectedContent = (editorInstance) => {
    const { from, to } = editorInstance.state.selection;
    const { doc } = editorInstance.state;
    try {
        if (doc.nodeAt(from)?.attrs?.isProtected === true) return true;
        if (from > 0 && doc.nodeAt(from - 1)?.attrs?.isProtected === true) return true;
        const $from = doc.resolve(from);
        for (let depth = $from.depth; depth >= 0; depth -= 1) {
            if ($from.node(depth).attrs?.isProtected === true) return true;
        }
        let hasProtected = false;
        doc.nodesBetween(from, to, (node) => {
            if (node.attrs?.isProtected === true) {
                hasProtected = true;
                return false;
            }
            return true;
        });
        return hasProtected;
    } catch (error) {
        return false;
    }
};

/** 选区内（或光标处）是否有指定类型的节点 */
const findNodeInSelection = (editorInstance, typeName, { checkBefore = false } = {}) => {
    const { from, to } = editorInstance.state.selection;
    const { doc } = editorInstance.state;
    let found = null;
    doc.nodesBetween(from, to, (node, pos) => {
        if (found) return false;
        if (node.type.name === typeName) {
            found = { node, pos };
            return false;
        }
        return true;
    });
    if (!found && from === to) {
        try {
            const nodeAt = doc.nodeAt(from);
            if (nodeAt && nodeAt.type.name === typeName) found = { node: nodeAt, pos: from };
        } catch (error) {
            // ignore
        }
    }
    if (!found && checkBefore && from > 0) {
        try {
            const nodeBefore = doc.nodeAt(from - 1);
            if (nodeBefore && nodeBefore.type.name === typeName) {
                found = { node: nodeBefore, pos: from - 1 };
            }
        } catch (error) {
            // ignore
        }
    }
    return found;
};

const collectBlockIds = (editorInstance, typeName) => {
    const { from, to } = editorInstance.state.selection;
    const ids = new Set();
    editorInstance.state.doc.nodesBetween(from, to, (node) => {
        if (node.type.name === typeName && node.attrs?.blockId) ids.add(node.attrs.blockId);
        return true;
    });
    return Array.from(ids);
};

/**
 * 面板内容：识别编辑器里选中的对象 → 切换面板模式 / 页签
 */
const RightPanelContent = () => {
    const {
        editor,
        uiState,
        commonState,
        textState,
        imgState,
        tableState,
        formulaState,
        rewriteTask,
        handleReset,
        handleReplaceContent,
        handleCancelPreview,
        selectionState,
    } = useRightPanelContext();

    const setFlags = useCallback(
        ({ image = false, table = false, formula = false }) => {
            imgState.isImageSelected = image;
            tableState.isTableSelected = table;
            formulaState.isFormulaSelected = formula;
        },
        [imgState, tableState, formulaState],
    );

    // ========== 选中检测 ==========

    useEffect(() => {
        if (!editor || commonState.isResetting || commonState.showPreview) return;

        const { from, to } = editor.state.selection;

        // 受保护内容（参考文献）：不提供任何修改
        if (isInProtectedContent(editor)) {
            commonState.mode = PANEL_MODE.EMPTY;
            textState.selectedText = '';
            textState.selectedBlockIds = [];
            textState.selectedRange = { from: null, to: null };
            setFlags({});
            return;
        }

        // 图片
        if (findNodeInSelection(editor, 'image')) {
            textState.selectedText = '已选中图片';
            textState.selectedBlockIds =
                selectionState.selectedBlockIds?.length > 0
                    ? selectionState.selectedBlockIds
                    : collectBlockIds(editor, 'image');
            textState.selectedRange = { from, to };
            textState.currentAction = ACTION_TYPE.MODIFY_IMAGE;
            commonState.mode = PANEL_MODE.IMAGE;
            uiState.activeTab = PANEL_TAB.SELECTION;
            setFlags({ image: true });
            return;
        }

        // 块级公式
        const formula = findNodeInSelection(editor, 'blockMath', { checkBefore: true });
        if (formula) {
            const latex = formula.node.attrs.latex || '';
            const formulaInfo = {
                node: formula.node,
                pos: formula.pos,
                type: 'block',
                latex,
                formulaText: `$$\n${latex}\n$$`,
                blockId: formula.node.attrs.blockId,
                detailId: formula.node.attrs.detailId,
            };
            textState.selectedText = formulaInfo.formulaText;
            textState.selectedBlockIds = formulaInfo.blockId ? [formulaInfo.blockId] : [];
            textState.selectedRange = { from, to };
            textState.currentAction = ACTION_TYPE.MODIFY_FORMULA;
            formulaState.formulaInfo = formulaInfo;
            commonState.mode = PANEL_MODE.FORMULA;
            uiState.activeTab = PANEL_TAB.SELECTION;
            setFlags({ formula: true });
            return;
        }

        // 表格
        if (findNodeInSelection(editor, 'table')) {
            textState.selectedText = '已选中表格';
            textState.selectedBlockIds =
                selectionState.selectedBlockIds?.length > 0
                    ? selectionState.selectedBlockIds
                    : collectBlockIds(editor, 'table');
            textState.selectedRange = { from, to };
            textState.currentAction = ACTION_TYPE.MODIFY_TABLE;
            commonState.mode = PANEL_MODE.TABLE;
            uiState.activeTab = PANEL_TAB.SELECTION;
            setFlags({ table: true });
            return;
        }

        // 标题 → 章节改稿
        if (selectionState.isHeadingSelected && selectionState.selectedHeadingInfo) {
            const info = selectionState.selectedHeadingInfo;
            textState.selectedText = '';
            textState.selectedRange = { from: null, to: null };
            textState.selectedBlockIds = [];
            commonState.mode = PANEL_MODE.REWRITE_CHAPTER;
            // 改稿进行中 / 已有结果时，不切换改稿对象
            if (rewriteTask.phase === REWRITE_PHASE.IDLE || rewriteTask.phase === REWRITE_PHASE.FAILED) {
                commonState.currentChapterInfo = {
                    node: info.node,
                    pos: info.pos,
                    detailId: info.detailId,
                    level: info.level,
                    blockId: info.blockId,
                };
            }
            uiState.activeTab = PANEL_TAB.CHAPTER;
            setFlags({});
            return;
        }

        // 文字选区
        const selectedText =
            selectionState.selectedText && selectionState.selectedText.trim()
                ? selectionState.selectedText
                : editor.state.doc.textBetween(from, to, ' ');
        if (selectedText && selectedText.trim()) {
            textState.selectedText = selectedText;
            textState.selectedRange =
                selectionState.selectedRange && selectionState.selectedRange.from !== null
                    ? selectionState.selectedRange
                    : { from, to };
            textState.selectedBlockIds =
                selectionState.selectedBlockIds?.length > 0
                    ? selectionState.selectedBlockIds
                    : extractBlockIdsFromSelection(editor);
            textState.currentAction = '';
            commonState.mode = PANEL_MODE.SELECTION;
            uiState.activeTab = PANEL_TAB.SELECTION;
            setFlags({});
            return;
        }

        // 什么都没选：局部修改回到空状态，但不清掉章节改稿的对象
        textState.selectedText = '';
        textState.selectedRange = { from: null, to: null };
        textState.selectedBlockIds = [];
        textState.currentAction = '';
        commonState.mode = PANEL_MODE.EMPTY;
        setFlags({});
    }, [
        editor,
        selectionState.version,
        selectionState.selectedText,
        selectionState.selectedBlockIds,
        selectionState.selectedRange,
        selectionState.isHeadingSelected,
        selectionState.selectedHeadingInfo,
        commonState,
        textState,
        formulaState,
        uiState,
        rewriteTask.phase,
        setFlags,
    ]);

    // ========== 快捷键 ==========

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (commonState.generating) return;
                if (commonState.showPreview) {
                    e.preventDefault();
                    handleCancelPreview();
                    return;
                }
                if (commonState.mode !== PANEL_MODE.EMPTY) {
                    e.preventDefault();
                    handleReset();
                }
                return;
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                if (commonState.generating) return;
                if (commonState.showPreview && textState.previewResult) {
                    e.preventDefault();
                    handleReplaceContent();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [
        commonState.mode,
        commonState.showPreview,
        commonState.generating,
        textState.previewResult,
        handleCancelPreview,
        handleReset,
        handleReplaceContent,
    ]);

    // ========== 渲染 ==========

    const chapterBusy = rewriteTask.isBusy;
    const chapterReady = rewriteTask.phase === REWRITE_PHASE.READY;
    const activeTab = uiState.activeTab;

    const renderSelectionPanel = () => {
        switch (commonState.mode) {
            case PANEL_MODE.SELECTION:
                return <SelectionPanel />;
            case PANEL_MODE.IMAGE:
                return <ImagePanel />;
            case PANEL_MODE.TABLE:
                return <TablePanel />;
            case PANEL_MODE.FORMULA:
                return <FormulaPanel />;
            default:
                return <EmptyPanel />;
        }
    };

    return (
        <div className={styles.toolPanel}>
            <div className={styles.toolPanelHeader}>
                <Segmented
                    block
                    value={activeTab}
                    onChange={(value) => {
                        uiState.activeTab = value;
                    }}
                    options={[
                        {
                            value: PANEL_TAB.CHAPTER,
                            label: (
                                <span className={styles.tabLabel}>
                                    <Sparkles size={14} />
                                    章节改稿
                                    {(chapterBusy || chapterReady) && (
                                        <Tooltip title={chapterBusy ? '改稿进行中' : '改稿结果待处理'}>
                                            <span
                                                className={`${styles.tabDot} ${chapterBusy ? styles.tabDotBusy : ''}`}
                                            />
                                        </Tooltip>
                                    )}
                                </span>
                            ),
                        },
                        {
                            value: PANEL_TAB.SELECTION,
                            label: (
                                <span className={styles.tabLabel}>
                                    <TextCursorInput size={14} />
                                    局部修改
                                    {commonState.mode !== PANEL_MODE.EMPTY &&
                                        commonState.mode !== PANEL_MODE.REWRITE_CHAPTER && (
                                            <span className={styles.tabDot} />
                                        )}
                                </span>
                            ),
                        },
                    ]}
                />
            </div>

            {commonState.showPreview && activeTab === PANEL_TAB.SELECTION && (
                <div className={styles.previewLockTip}>
                    请先处理下方的生成结果（应用或放弃），再进行其他操作
                </div>
            )}

            <div className={styles.toolPanelBody}>
                <div
                    className={styles.tabPane}
                    style={{ display: activeTab === PANEL_TAB.CHAPTER ? 'block' : 'none' }}
                >
                    <ChapterRewritePanel />
                </div>
                <div
                    className={styles.tabPane}
                    style={{ display: activeTab === PANEL_TAB.SELECTION ? 'block' : 'none' }}
                >
                    {renderSelectionPanel()}
                </div>
            </div>
        </div>
    );
};

export default EditorRightPanel;
